// import { FormData } from "../pages/NoteWorksheet";
import PaperBrokerSubmission, {
  NoteInfo,
  OwnerInfo,
  PropertyInfo,
  SubmissionUserType,
} from "../interfaces/paperBrokerSubmission";

interface FormData {
  isTestMode: boolean;
  submissionType: "owner" | "birddog" | "broker";
  ownerInfo: OwnerInfo;
  propertyInfo: PropertyInfo;
  noteInfo: NoteInfo;
}

/**
 * Formats the NoteWorksheet form data to match the PaperBrokerSubmission interface
 * @param formData The form data from the NoteWorksheet
 * @returns Formatted data that matches the PaperBrokerSubmission interface
 */
export const formatPaperBrokerSubmissionData = (
  formData: FormData
): PaperBrokerSubmission => {
  const { submissionType, isTestMode, ownerInfo, propertyInfo, noteInfo } =
    formData;

  // Convert submissionType to SubmissionUserType
  const submission_user_type: SubmissionUserType = submissionType;
  const is_test_mode = formData.isTestMode;

  // Format dates
  const note_date = noteInfo.noteDate ? new Date(noteInfo.noteDate) : undefined;
  const next_payment_date = noteInfo.nextPaymentDate
    ? new Date(noteInfo.nextPaymentDate)
    : undefined;
  const balloon_payment_date = noteInfo.balloonPaymentDate
    ? new Date(noteInfo.balloonPaymentDate)
    : undefined;
  const maturity_date = noteInfo.maturityDate
    ? new Date(noteInfo.maturityDate)
    : undefined;
  const total_debt = noteInfo.totalDebt
    ? parseFloat(noteInfo.totalDebt)
    : undefined;

  // Convert string values to appropriate types
  const year_built = propertyInfo.yearBuilt
    ? parseInt(propertyInfo.yearBuilt)
    : undefined;
  const acreage = propertyInfo.acreage || undefined;
  const estimated_value = propertyInfo.estimatedValue
    ? parseFloat(propertyInfo.estimatedValue)
    : undefined;
  const property_sale_price = noteInfo.propertySalePrice
    ? parseFloat(noteInfo.propertySalePrice)
    : undefined;
  const down_payment_amount = noteInfo.downPaymentAmount
    ? parseFloat(noteInfo.downPaymentAmount)
    : undefined;
  const original_starting_balance = noteInfo.originalStartingBalance
    ? parseFloat(noteInfo.originalStartingBalance)
    : undefined;
  const current_principle_balance = noteInfo.currentPrincipleBalance
    ? parseFloat(noteInfo.currentPrincipleBalance)
    : undefined;
  const interest_rate = noteInfo.interestRate
    ? parseFloat(noteInfo.interestRate)
    : undefined;
  const payment_amount = noteInfo.paymentAmount
    ? parseFloat(noteInfo.paymentAmount)
    : undefined;
  const note_asking_price = noteInfo.noteAskingPrice
    ? parseFloat(noteInfo.noteAskingPrice)
    : undefined;

  // Convert string boolean values to actual booleans
  const has_balloon_payment = noteInfo.hasBalloonPayment === "true";
  const payments_current = noteInfo.paymentsCurrent === "true";
  const is_first_position = noteInfo.isFirstPosition === "true";

  // Create the submission object based on the submission type
  const submission: PaperBrokerSubmission = {
    submission_user_type,
    is_test_mode,

    // Property Info fields
    property_address: propertyInfo.propertyAddress,
    property_city: propertyInfo.city,
    property_state: propertyInfo.state,
    property_zip_code: propertyInfo.zipCode,
    property_type: propertyInfo.propertyType,
    year_built,
    acreage,
    property_description: propertyInfo.propertyDescription,
    occupancy: propertyInfo.occupancy,
    estimated_value,

    // Note Info fields
    note_date,
    property_sale_price,
    down_payment_amount,
    original_starting_balance,
    current_principle_balance,
    interest_rate,
    payment_amount,
    payment_options: noteInfo.paymentOptions,
    next_payment_date,
    maturity_date,
    has_balloon_payment,
    balloon_payment_date: has_balloon_payment
      ? balloon_payment_date
      : undefined,
    payments_current,
    total_debt: noteInfo.paymentsCurrent === "false" ? total_debt : undefined,
    payment_collector: noteInfo.paymentCollector,
    third_party_servicer_name:
      noteInfo.paymentCollector === "Third Party Servicer"
        ? noteInfo.thirdPartyServicerName
        : undefined,
    is_first_position,
    note_asking_price,
    special_circumstances: noteInfo.specialCircumstances,
    documents:
      noteInfo.documents && noteInfo.documents.length > 0
        ? noteInfo.documents
        : undefined,
    additional_comments: noteInfo.additionalComments,
  };

  // Add owner or user info based on submission type
  if (submissionType === "owner") {
    submission.owner_first_name = ownerInfo.firstName;
    submission.owner_last_name = ownerInfo.lastName;
    submission.owner_email = ownerInfo.email;
    submission.owner_phone = ownerInfo.phone;
    submission.owner_mailing_address = ownerInfo.mailingAddress;
    submission.owner_city = ownerInfo.city;
    submission.owner_state = ownerInfo.state;
    submission.owner_zip_code = ownerInfo.zipCode;
  } else if (submissionType === "birddog") {
    // For birddog, we need both user info and owner info
    submission.user_first_name = ownerInfo.yourFirstName;
    submission.user_last_name = ownerInfo.yourLastName;
    submission.user_email = ownerInfo.yourEmail;
    submission.user_phone = ownerInfo.yourPhone;
    submission.user_mailing_address = ownerInfo.yourMailingAddress;
    submission.user_city = ownerInfo.yourCity;
    submission.user_state = ownerInfo.yourState;
    submission.user_zip_code = ownerInfo.yourZipCode;

    submission.owner_first_name = ownerInfo.ownerFirstName;
    submission.owner_last_name = ownerInfo.ownerLastName;
    submission.owner_email = ownerInfo.ownerEmail;
    submission.owner_phone = ownerInfo.ownerPhone;
    submission.owner_mailing_address = ownerInfo.ownerMailingAddress;
    submission.owner_city = ownerInfo.ownerCity;
    submission.owner_state = ownerInfo.ownerState;
    submission.owner_zip_code = ownerInfo.ownerZipCode;
  } else if (submissionType === "broker") {
    // For broker, we only need user info
    submission.user_first_name = ownerInfo.yourFirstName;
    submission.user_last_name = ownerInfo.yourLastName;
    submission.user_email = ownerInfo.yourEmail;
    submission.user_phone = ownerInfo.yourPhone;
    submission.user_mailing_address = ownerInfo.yourMailingAddress;
    submission.user_city = ownerInfo.yourCity;
    submission.user_state = ownerInfo.yourState;
    submission.user_zip_code = ownerInfo.yourZipCode;
  }

  return submission;
};
